/**
 * Starting a run (`soloStart`): the one place that does it for the start page and the finale. The call is "start another run": it opens a new run of the
 * program (scored like the others), or the run nobody has played yet. The person's memberships are read again first, because the new
 * run is a new cohort and the chair would otherwise open on a stale principal with no team.
 */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { ErrorBody } from "@bsim/contracts";
import { useApi } from "../api/context";
import { navigate } from "../shell/history";
import { playPath } from "../solo/run";

export function useStartRun(): { readonly start: (programId: string, name?: string) => Promise<void>; readonly pending: boolean; readonly error: ErrorBody["error"] | null } {
  const api = useApi();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ErrorBody["error"] | null>(null);
  const start = async (programId: string, name?: string): Promise<void> => {
    setPending(true);
    setError(null);
    const r = await api.call("soloStart", undefined, name === undefined || name.trim() === "" ? { programId } : { programId, name: name.replace(/\s+/g, " ").trim() }, { "idempotency-key": globalThis.crypto.randomUUID() });
    if (r.ok) await queryClient.invalidateQueries({ queryKey: ["authMe"], refetchType: "all" });
    setPending(false);
    if (r.ok) navigate(playPath(r.value, 0)); else setError(r.error);
  };
  return { start, pending, error };
}

/** The words under "Start another run": what the new run is. */
export const START_ANOTHER_NOTE = "A new run starts from the opening position and keeps this one. It is scored too.";
